import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../../ui';
import { callFunction } from '../../shared/functions';
import { DemoInviteLink, useRevealedLink } from './DemoInviteLink';

/**
 * Resend and revoke, the two halves of "an invitation is revocable".
 *
 * Both go through `invite-client` rather than touching a table, because no role
 * has any grant on `client_invite` at all — the coach cannot read her own
 * clients' invitations, let alone anyone else's, and that is deliberate: the
 * table holds a credential's hash and nothing about it belongs in a browser.
 *
 * Resend replaces the open invitation rather than adding a second one. Two live
 * links for one person is one more than anybody can revoke reliably — which is
 * also why resending is the only way back to a link the coach has lost: the old
 * one stops working the moment the new one is minted.
 *
 * Resending an address whose domain ends in `.test` shows the new link, on this
 * screen and only until it is left; every other address shows nothing, in every
 * environment. `DemoInviteLink` says why, and checks the address itself.
 */
export function InviteActions({ clientId, email }: { clientId: string; email: string }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState<string | null>(null);
  const { revealed, reveal, clear } = useRevealedLink();

  const act = useMutation({
    mutationFn: (action: 'resend' | 'revoke') =>
      callFunction<{ ok: true; link?: string }>('invite-client', { action, clientId }),
    onSuccess: async (data, action) => {
      setNote(action === 'resend' ? 'A new invitation is on its way.' : 'Invitation revoked.');
      if (action === 'resend') reveal(email, data.link);
      await queryClient.invalidateQueries({ queryKey: ['roster'] });
    },
  });

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          disabled={act.isPending}
          onClick={() => {
            setNote(null);
            clear();
            act.mutate('resend');
          }}
        >
          Resend
        </Button>
        <Button
          variant="ghost"
          disabled={act.isPending}
          onClick={() => {
            setNote(null);
            clear();
            act.mutate('revoke');
          }}
        >
          Revoke
        </Button>
      </div>

      {act.isError ? (
        <p role="alert" className="text-danger text-xs">
          {act.error.message}
        </p>
      ) : null}

      {note === null ? null : (
        <p role="status" className="text-ink-muted text-xs">
          {note}
        </p>
      )}

      {revealed === null ? null : (
        <div className="w-full">
          <DemoInviteLink revealed={revealed} />
        </div>
      )}
    </div>
  );
}
